import { Formik } from 'formik';
import { useMemo, useRef } from 'react';

import { FeatureFlags } from '@constants/featureFlags';
import {
  BundleBasketFragment,
  EnergyQuoteType,
  Occupancy,
  ProductType,
} from '@generated/gql/graphql';
import { useBasketQuery } from '@hooks/useBasketQuery.ts';
import { useDetectMidJourneyLogin } from '@hooks/useDetectMidJourneyLogin';
import { useFeatureFlag } from '@hooks/useFeatureFlag';
import { usePartnerConnectors } from '@hooks/usePartnerConnectors.ts';
import { useRedirectOnInvalidSelection } from '@hooks/useRedirectOnInvalidSelection';
import { useDetailsContext } from '@providers/basket/DetailsProvider/Context';
import { useTrackingContextRefresh } from '@tracking/hooks/useTrackingContextRefresh.ts';
import { getCashbackSavingsValue } from '@utils/formatBasket/cashback.ts';

import { DetailsBasket } from '../Basket';
import Form from './Form';
import './styles.scss';
import { useSubmit } from './useSubmit';
import { getIsDigitalHomePhoneSelected } from './utils/getIsBroadbandOnly';
import { validate } from './validate.ts';

const Details = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const { details } = useDetailsContext();

  const { isLoading: loadingData, basketData, error } = useBasketQuery();

  const { connectors } = usePartnerConnectors();
  const { value: isPaperBillingEnabled } = useFeatureFlag(
    FeatureFlags.PAPER_BILLING_ENABLED,
    false
  );

  const occupancyType = basketData?.basket?.details?.occupancyType;

  const isFixedSelected =
    basketData?.basket?.energy?.selectedQuote?.quoteType ===
    EnergyQuoteType.Fixed;

  const selectedQuotes = useMemo(
    () => basketData?.basket?.selectedQuotes ?? [],
    [basketData?.basket?.selectedQuotes]
  );

  const isDigitalHomePhoneSelected = useMemo(
    () => getIsDigitalHomePhoneSelected(selectedQuotes),
    [selectedQuotes]
  );

  const productTypes = selectedQuotes.map((q) => q.product.type);
  const canSwitch = productTypes.includes(ProductType.Broadband);

  useDetectMidJourneyLogin({ basketData, loading: loadingData });
  useRedirectOnInvalidSelection(
    basketData?.basket as BundleBasketFragment,
    'yourDetails'
  );

  // Ensures that the tracking context is set after page refresh
  useTrackingContextRefresh()(basketData, loadingData);

  const { handleSubmit, isLoading } = useSubmit({
    basket: basketData?.basket || null,
    productTypes,
    isDigitalHomePhoneSelected,
  });

  return (
    <Formik
      initialValues={details}
      validate={(values) =>
        validate(values, {
          isPaperBillingEnabled,
          isSelectedBroadbandOnly: isDigitalHomePhoneSelected,
          canSwitch,
        })
      }
      validateOnBlur={false}
      onSubmit={(values, { setFieldError }) =>
        handleSubmit(
          values,
          occupancyType as unknown as Occupancy,
          connectors,
          setFieldError
        )
      }
    >
      <section>
        <div className="wrapper">
          <div className="details__container">
            <section>
              <Form
                connectors={connectors}
                attributionDetails={basketData?.attributionDetails ?? null}
                isLoading={isLoading}
                productTypes={productTypes}
                ref={formRef}
                selectedQuotes={selectedQuotes}
                isFixedSelected={isFixedSelected}
                isDigitalHomePhoneSelected={isDigitalHomePhoneSelected}
              />
            </section>
            <DetailsBasket
              loading={loadingData}
              cashbackSavingsValue={getCashbackSavingsValue(
                basketData?.cashbackCalculatorDetails ?? null
              )}
              error={error}
              basket={basketData?.basket as BundleBasketFragment}
              isTrackingEnabled
            />
          </div>
        </div>
      </section>
    </Formik>
  );
};

export { Details };
